/**
 * The firm a task is owed by, resolved from the one roster query (`KLQ-20260820-007`).
 *
 * Every surface that names the firm reads this — the detail and the register row — over
 * `partiesQueryOptions`, which is one fetch per project shared by key. Never a request per
 * row: the register draws hundreds of them and the roster is tens of firms.
 */

import type { ProjectParty } from '@/features/people';
import type { Translate } from '@/lib/i18n';

import styles from './Tasks.module.css';

export interface FirmOnTask {
  /**
   * The firm's name, or `null` when this screen cannot name it: the roster is still loading,
   * or the firm was archived after the task was assigned (the roster read leaves archived rows
   * out). The caller renders the neutral *unknown firm* label — never nothing, because a task
   * a firm owes must not read as a task nobody owes.
   */
  readonly name: string | null;
  /**
   * `workspace_ref_id` is `null`: the firm holds no Kalqa account, so assigning it notified
   * nobody and nobody there can see the task. `false` when the firm is unknown — the mark is
   * a claim, and an unresolved row is not evidence for it.
   */
  readonly offSystem: boolean;
}

/** `null` when the task is owed by no firm at all. */
export function firmOf(
  parties: readonly ProjectParty[] | undefined,
  partyId: string | null,
): FirmOnTask | null {
  if (partyId === null) return null;
  const party = parties?.find((row) => row.id === partyId);
  if (party === undefined) return { name: null, offSystem: false };
  return { name: party.name, offSystem: (party.workspace_ref_id ?? null) === null };
}

/**
 * The off-system mark: text, not a colour — the firm holds no Kalqa account, so assigning it
 * notified nobody and nobody there sees the task (`KLQ-20260820-007` §3). One component for the
 * detail and the register row, so the two cannot say it differently.
 */
export function OffSystemMark({ t }: { readonly t: Translate<'tasks'> }) {
  return <span className={styles.offSystem}>{t('assign.firmOffSystem')}</span>;
}

/** The firm's name with its mark, or *no firm*. */
export function FirmName({
  firm,
  t,
}: {
  readonly firm: FirmOnTask | null;
  readonly t: Translate<'tasks'>;
}) {
  if (firm === null) return <>{t('assign.firmNone')}</>;
  return (
    <>
      {firm.name ?? t('assign.firmUnknown')}
      {firm.offSystem ? (
        <>
          {' '}
          <OffSystemMark t={t} />
        </>
      ) : null}
    </>
  );
}
